import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BarChart3, Users, DollarSign, Activity, Search } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { analyticsApi, type DashboardStats } from "@/services/api/analytics.api";

const t = {
  en: {
    title: "Performance & Search Stats",
    subtitle: "Aggregate KPIs across deals, staff, revenue, users and activity.",
    deals: "Deals",
    totalDeals: "Total deals",
    totalValue: "Total value",
    avgValue: "Average value",
    revenue: "Revenue",
    totalRevenue: "Total revenue",
    completed: "Completed payments",
    pending: "Pending payments",
    failed: "Failed payments",
    users: "Users",
    totalUsers: "Total users",
    activity: "Activity",
    staff: "Staff Performance",
    assignments: "Assignments",
    inProgress: "In progress",
    avgDays: "Avg days",
    loading: "Loading…",
    error: "Unable to load analytics. The backend may be offline.",
    stages: "Deals by Pipeline Stage",
  },
  fr: {
    title: "Performance & Stats de recherche",
    subtitle: "KPIs agrégés sur les dossiers, le staff, les revenus, les utilisateurs et l'activité.",
    deals: "Dossiers",
    totalDeals: "Total dossiers",
    totalValue: "Valeur totale",
    avgValue: "Valeur moyenne",
    revenue: "Revenus",
    totalRevenue: "Revenus totaux",
    completed: "Paiements complétés",
    pending: "Paiements en attente",
    failed: "Paiements échoués",
    users: "Utilisateurs",
    totalUsers: "Total utilisateurs",
    activity: "Activité",
    staff: "Performance Staff",
    assignments: "Assignations",
    inProgress: "En cours",
    avgDays: "Jours moyens",
    loading: "Chargement…",
    error: "Impossible de charger les analytics. Le backend est peut-être hors ligne.",
    stages: "Dossiers par étape du pipeline",
  },
};

function StatCard({ label, value, icon: Icon }: { label: string; value: string | number; icon: any }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
          </div>
          <Icon className="h-8 w-8 text-amber-500" />
        </div>
      </CardContent>
    </Card>
  );
}

export default function PerformanceStats() {
  const { language } = useLanguage();
  const tr = t[language as "en" | "fr"] || t.en;
  const [data, setData] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    analyticsApi
      .getDashboard()
      .then((d) => setData(d))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="p-6 space-y-6">
      <header>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <BarChart3 className="h-6 w-6 text-amber-500" />
          {tr.title}
        </h1>
        <p className="text-muted-foreground mt-1">{tr.subtitle}</p>
      </header>

      {loading && <p className="text-muted-foreground">{tr.loading}</p>}
      {error && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-destructive">{tr.error}</p>
          </CardContent>
        </Card>
      )}

      {data && (
        <>
          {/* Deals */}
          <section>
            <h2 className="text-lg font-semibold mb-3">{tr.deals}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatCard label={tr.totalDeals} value={data.deals.totalDeals} icon={BarChart3} />
              <StatCard label={tr.totalValue} value={`$${(data.deals.totalValue || 0).toLocaleString()}`} icon={DollarSign} />
              <StatCard label={tr.avgValue} value={`$${Math.round(data.deals.avgDealValue || 0).toLocaleString()}`} icon={Activity} />
            </div>
          </section>

          {/* Pipeline stages */}
          <Card>
            <CardHeader>
              <CardTitle>{tr.stages}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {Object.entries(data.deals.stages || {}).map(([stage, count]) => (
                  <Badge key={stage} variant="secondary" className="text-sm">
                    {stage}: <span className="ml-1 font-bold">{count as number}</span>
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Revenue */}
          <section>
            <h2 className="text-lg font-semibold mb-3">{tr.revenue}</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <StatCard label={tr.totalRevenue} value={`$${(data.revenue.totalRevenue || 0).toLocaleString()}`} icon={DollarSign} />
              <StatCard label={tr.completed} value={data.revenue.completedPayments || 0} icon={DollarSign} />
              <StatCard label={tr.pending} value={data.revenue.pendingPayments || 0} icon={DollarSign} />
              <StatCard label={tr.failed} value={data.revenue.failedPayments || 0} icon={DollarSign} />
            </div>
          </section>

          {/* Users */}
          <section>
            <h2 className="text-lg font-semibold mb-3">{tr.users}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <StatCard label={tr.totalUsers} value={data.users.totalUsers || 0} icon={Users} />
              <Card>
                <CardContent className="pt-6">
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(data.users.roleCounts || {}).map(([role, count]) => (
                      <Badge key={role} variant="outline">
                        {role}: <span className="ml-1 font-bold">{count as number}</span>
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Activity */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="h-5 w-5" />
                {tr.activity}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {Object.entries(data.activity || {}).map(([key, count]) => (
                  <Badge key={key} variant="secondary">
                    {key}: <span className="ml-1 font-bold">{count as number}</span>
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Staff Performance */}
          <Card>
            <CardHeader>
              <CardTitle>{tr.staff}</CardTitle>
              <CardDescription>{data.staff.length} staff members</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-muted-foreground border-b">
                    <tr>
                      <th className="py-2">Staff</th>
                      <th>{tr.assignments}</th>
                      <th>{tr.completed}</th>
                      <th>{tr.inProgress}</th>
                      <th>{tr.avgDays}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.staff.map((s) => (
                      <tr key={s.staffId} className="border-b">
                        <td className="py-2 font-mono text-xs">{s.staffId}</td>
                        <td>{s.totalAssignments}</td>
                        <td>{s.completed}</td>
                        <td>{s.inProgress}</td>
                        <td>{s.avgCompletionDays?.toFixed(1) ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
